import { LOCALES, SUPPORTED_LOCALES, toLocale, type Locale } from '@zuruflex/i18n';
import { Check, Languages } from 'lucide-react';
import { cn } from '../../lib/cn.js';
import { Button } from '../ui/button.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu.js';

export interface LanguageSwitcherProps {
  /** Accepts a raw i18next language, including regional tags like `fr-CA`. */
  value: string;
  onChange: (locale: Locale) => void;
  /** `full` shows the language name, `compact` shows just the code. */
  variant?: 'full' | 'compact';
  /** Heading inside the menu, already translated by the caller. */
  label?: string;
  className?: string;
}

/**
 * Languages are listed in their own names — someone looking for Kinyarwanda
 * is looking for "Kinyarwanda", not for "rw" or a flag they have to decode.
 */
export function LanguageSwitcher({
  value,
  onChange,
  variant = 'full',
  label,
  className,
}: LanguageSwitcherProps) {
  // i18next reports whatever the browser asked for, which may be `en-GB`;
  // narrowing here keeps the component from indexing LOCALES with a miss.
  const active = toLocale(value);
  const current = LOCALES[active];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn('gap-2', className)}
          aria-label={label ?? 'Language'}
        >
          <Languages />
          {variant === 'full' ? current.nativeName : active.toUpperCase()}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end">
        {label ? <DropdownMenuLabel>{label}</DropdownMenuLabel> : null}
        {SUPPORTED_LOCALES.map((locale) => (
          <DropdownMenuItem
            key={locale}
            onSelect={() => onChange(locale)}
            className={locale === active ? 'font-semibold' : undefined}
          >
            <span className="w-4 text-center">{locale === active ? <Check /> : null}</span>
            {LOCALES[locale].nativeName}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
